import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, ResultTable } from '../_fitnessHelpers';

export default function OneRepMaxCalc() {
  const [tab, setTab] = useState(0);
  const [weight, setWeight] = useState(200);
  const [reps, setReps] = useState(5);
  const [formula, setFormula] = useState('avg');
  const clear = () => { setWeight(200); setReps(5); setFormula('avg'); };

  const unit = tab === 0 ? 'lbs' : 'kg';
  const c = useMemo(() => {
    const w = +weight || 0, r = +reps || 0;
    const epley = w * (1 + r/30);
    const brzycki = r < 37 ? w * 36/(37 - r) : 0;
    const lombardi = w * Math.pow(Math.max(r,1), 0.10);
    const oconner = w * (1 + r/40);
    const mayhew = w * 100 / (52.2 + 41.9 * Math.exp(-0.055 * r));
    const wathan = w * 100 / (48.8 + 53.8 * Math.exp(-0.075 * r));
    const values = { epley, brzycki, lombardi, oconner, mayhew, wathan };
    const avg = (epley + brzycki + lombardi + oconner + mayhew + wathan) / 6;
    const chosen = formula === 'avg' ? avg : values[formula];
    return { values, avg, chosen };
  }, [weight, reps, formula]);

  const pcts = [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50];

  return (
    <CalcShell look="cnet" title="One Rep Max Calculator" subtitle="Estimate one-rep maximum from a lifted weight and reps completed.">
      <CalcNetPanel
        slug="orm"
        activeTab={tab}
        onTabChange={setTab}
        belowGrid={
          <div className="calc-card p-6">
            <h3 className="font-bold text-slate-900 mb-3">% of 1RM (based on {c.chosen.toFixed(1)} {unit})</h3>
            {/* Bar graph of formula results */}
            <div className="mb-4" data-testid="orm-bar">
              <div className="text-xs font-semibold text-slate-600 mb-2">Comparison of 1RM formulas</div>
              <svg viewBox="0 0 500 200" className="w-full max-w-lg">
                {[
                  ['Epley', c.values.epley, '#3b82f6'],
                  ['Brzycki', c.values.brzycki, '#10b981'],
                  ['Lombardi', c.values.lombardi, '#f59e0b'],
                  ["O'Conner", c.values.oconner, '#8b5cf6'],
                  ['Mayhew', c.values.mayhew, '#ef4444'],
                  ['Wathan', c.values.wathan, '#64748b'],
                ].map(([lbl, v, color], i) => {
                  const maxV = Math.max(c.values.epley, c.values.brzycki, c.values.lombardi, c.values.oconner, c.values.mayhew, c.values.wathan) || 1;
                  const h = Math.max(0, (v / maxV) * 140);
                  return (
                    <g key={i}>
                      <rect x={30 + i * 78} y={180 - h} width="58" height={h} fill={color} rx="3" />
                      <text x={30 + i * 78 + 29} y={195} fontSize="10" fill="#334155" textAnchor="middle">{lbl}</text>
                      <text x={30 + i * 78 + 29} y={180 - h - 4} fontSize="11" fill="#0f172a" textAnchor="middle" fontWeight="600">{v.toFixed(0)}</text>
                    </g>
                  );
                })}
                <line x1="20" y1="180" x2="490" y2="180" stroke="#94a3b8" />
              </svg>
            </div>
            <table className="w-full text-sm border-collapse max-w-md">
              <thead><tr>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-left border">Percent of 1RM</th>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-right border">Weight ({unit})</th>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-right border">Reps of 1RM</th>
              </tr></thead>
              <tbody>
                {pcts.map((p, i) => (
                  <tr key={p} className={i%2? 'bg-slate-50':''}>
                    <td className="border px-3 py-1.5">{p}%</td>
                    <td className="border px-3 py-1.5 text-right">{(c.chosen * p/100).toFixed(1)}</td>
                    <td className="border px-3 py-1.5 text-right">{p===100?1:p>=95?2:p>=90?4:p>=85?6:p>=80?8:p>=75?10:p>=70?12:p>=65?16:p>=60?20:p>=55?24:'≥30'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
        settings={
          <>
            <Row label="Formula">
              <select value={formula} onChange={e => setFormula(e.target.value)} data-testid="orm-formula"
                className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option value="avg">Average of all</option>
                <option value="epley">Epley</option>
                <option value="brzycki">Brzycki</option>
                <option value="lombardi">Lombardi</option>
                <option value="oconner">O'Conner</option>
                <option value="mayhew">Mayhew et al.</option>
                <option value="wathan">Wathan</option>
              </select>
            </Row>
          </>
        }
        right={
          <div>
            <div className="text-lg text-slate-800">Your estimated 1 Rep Max is</div>
            <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="orm-value">{c.chosen.toFixed(1)} {unit}</div>
            <p className="mt-3 text-sm text-slate-700 font-semibold">By formula</p>
            <ResultTable
              testid="orm-formulas-table"
              headers={['Formula', '1RM']}
              rows={[
                ['Epley', `${c.values.epley.toFixed(1)} ${unit}`],
                ['Brzycki', `${c.values.brzycki.toFixed(1)} ${unit}`],
                ['Lombardi', `${c.values.lombardi.toFixed(1)} ${unit}`],
                ["O'Conner", `${c.values.oconner.toFixed(1)} ${unit}`],
                ['Mayhew et al.', `${c.values.mayhew.toFixed(1)} ${unit}`],
                ['Wathan', `${c.values.wathan.toFixed(1)} ${unit}`],
              ]}
            />
          </div>
        }
      >
        <Row label="Weight lifted"><UnitInput value={weight} onChange={setWeight} unit={unit} testid="orm-w" /></Row>
        <Row label="Repetitions"><UnitInput value={reps} onChange={setReps} testid="orm-r" /></Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Epley: 1RM = weight × (1 + reps/30) = ${weight} × (1 + ${reps}/30) = <b>${c.values.epley.toFixed(1)}</b>`,
        `Brzycki: 1RM = weight × 36 / (37 − reps) = <b>${c.values.brzycki.toFixed(1)}</b>`,
        `Reported (${formula}): <b>${c.chosen.toFixed(1)} ${unit}</b>`,
      ]} />
    </CalcShell>
  );
}
